import { computeLayerPosition, type LayerMargins } from './layerPosition'

/**
 * Positions Astryx layers (Popover, DropdownMenu, Selector, Tooltip, HoverCard, and the
 * typeahead dropdown inside Tokenizer) on browsers that lack CSS anchor positioning.
 *
 * Astryx's `useLayer` puts every layer in the top layer via the Popover API and then
 * positions it *purely* in CSS, with `position-anchor` + `position-area` +
 * `position-try-fallbacks`. Browsers that support the Popover API but not anchor
 * positioning -- Firefox, and Safari before 26 -- drop those three declarations, and
 * because `useLayer`'s base style also zeroes the UA `margin: auto` that would otherwise
 * centre a popover, what is left is the UA `inset: 0`: every menu and dropdown lands in
 * the top-left corner of the viewport instead of next to its trigger.
 *
 * The declarations are unreadable as CSS in those browsers but they are not lost:
 * `useLayer` and React both assign them through the CSSOM (`el.style.positionArea = ...`),
 * and assigning an unsupported property just leaves a plain JS property on the style
 * object. So the anchor wiring can still be read back off `element.style` and turned into
 * real coordinates here.
 */

type StyleKey = 'anchorName' | 'positionAnchor' | 'positionArea'

interface TrackedLayer {
  anchor: HTMLElement
  resizeObserver: ResizeObserver
}

const trackedLayers = new Map<HTMLElement, TrackedLayer>()

/**
 * Is `wanted` one of the anchor names in a CSS `anchor-name` value? The value is a
 * comma-separated list -- Astryx supports several layers anchored to one element -- so
 * this has to compare whole entries, never substrings.
 */
export function hasAnchorName(anchorNameValue: string, wanted: string): boolean {
  return anchorNameValue
    .split(',')
    .map((name) => name.trim())
    .includes(wanted)
}

/**
 * True when the browser positions Astryx's layers on its own and this fallback is
 * unnecessary.
 *
 * The `position-area` probes use the actual `self-*` keyword family `useLayer` emits
 * rather than a generic value: an implementation that parses `block-start` but not
 * `self-block-start` computes the area to `none`, which drops the layer into the viewport
 * corner just as thoroughly as having no anchor positioning at all.
 */
export function isAnchorPositioningSupported(): boolean {
  if (typeof CSS === 'undefined' || typeof CSS.supports !== 'function') return false
  return (
    CSS.supports('anchor-name', '--a') &&
    CSS.supports('position-area', 'self-block-start span-self-inline-end') &&
    CSS.supports('position-area', 'self-inline-start span-self-block-end')
  )
}

/**
 * Read a CSS-anchor-positioning value off an element. Where the browser understands the
 * property this returns the real declaration; where it does not, it returns the plain JS
 * property left behind by the assignment, which is the value Astryx intended.
 */
function readAnchorStyle(element: Element, key: StyleKey): string {
  const style = (element as HTMLElement).style as unknown as Record<string, unknown> | undefined
  const value = style?.[key]
  return typeof value === 'string' ? value : ''
}

function findAnchorElement(anchorId: string): HTMLElement | null {
  for (const element of document.querySelectorAll<HTMLElement>('*')) {
    if (hasAnchorName(readAnchorStyle(element, 'anchorName'), anchorId)) {
      return element
    }
  }
  return null
}

function readMargins(computed: CSSStyleDeclaration): LayerMargins {
  const px = (value: string): number => {
    const parsed = Number.parseFloat(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return {
    top: px(computed.marginTop),
    right: px(computed.marginRight),
    bottom: px(computed.marginBottom),
    left: px(computed.marginLeft)
  }
}

/**
 * Astryx sizes its listbox and menu surfaces with `min-width: anchor-size(width)` so a
 * dropdown is never narrower than the control it belongs to. That declaration dies with
 * the rest of anchor positioning, leaving e.g. the genre typeahead visibly narrower than
 * its input, so restore it from the measured anchor.
 *
 * Deliberately limited to listbox/menu surfaces: they are the ones Astryx declares it on.
 * Tooltips and hover cards get no `anchor-size` and must keep their content width.
 */
function applyAnchorMinWidth(layer: HTMLElement, anchorWidth: number): void {
  if (!layer.querySelector('[role="listbox"], [role="menu"]')) return
  layer.style.minWidth = `${anchorWidth}px`
}

/** Returns false when the anchor has no geometry to place against. */
function positionLayer(layer: HTMLElement, anchor: HTMLElement): boolean {
  const anchorRect = anchor.getBoundingClientRect()
  // A trigger that has been unmounted or hidden gives no usable geometry. On a
  // reposition, keeping the previous coordinates beats slamming the layer somewhere new;
  // on the initial placement the caller undoes the off-screen park instead.
  if (anchorRect.width === 0 && anchorRect.height === 0) return false

  // Before measuring: the width feeds straight into the placement below.
  applyAnchorMinWidth(layer, anchorRect.width)

  const computed = window.getComputedStyle(layer)

  const { top, left } = computeLayerPosition({
    anchor: {
      top: anchorRect.top,
      left: anchorRect.left,
      width: anchorRect.width,
      height: anchorRect.height
    },
    // offsetWidth/Height, not getBoundingClientRect: Astryx layers animate in with a
    // scale/slide transform, and a transformed rect would measure the animation frame
    // rather than the layer's real size.
    layer: { width: layer.offsetWidth, height: layer.offsetHeight },
    viewport: {
      width: document.documentElement.clientWidth,
      height: document.documentElement.clientHeight
    },
    positionArea: readAnchorStyle(layer, 'positionArea') || null,
    direction: computed.direction === 'rtl' ? 'rtl' : 'ltr',
    margins: readMargins(computed)
  })

  layer.style.position = 'fixed'
  layer.style.top = `${top}px`
  layer.style.left = `${left}px`
  layer.style.right = 'auto'
  layer.style.bottom = 'auto'
  return true
}

/**
 * Park the layer far off-screen for the moment between the popover becoming visible and
 * our placing it. `toggle` is dispatched from a queued task, not synchronously from
 * `showPopover()`, so without this the browser is free to paint a frame with the UA
 * `inset: 0` still in effect -- the layer flashes in the top-left corner, which is the
 * very thing this module exists to prevent.
 *
 * Off-screen rather than `visibility: hidden` because Astryx moves focus into the layer
 * as it opens, and a `visibility: hidden` element cannot take focus.
 */
function parkOffScreen(layer: HTMLElement): void {
  layer.style.position = 'fixed'
  layer.style.top = '-10000px'
  layer.style.left = '-10000px'
  layer.style.right = 'auto'
  layer.style.bottom = 'auto'
}

function clearInlinePosition(layer: HTMLElement): void {
  layer.style.position = ''
  layer.style.top = ''
  layer.style.left = ''
  layer.style.right = ''
  layer.style.bottom = ''
  layer.style.minWidth = ''
}

function repositionAll(): void {
  for (const [layer, tracked] of trackedLayers) {
    if (!layer.isConnected || !layer.matches(':popover-open')) {
      untrack(layer)
      continue
    }
    positionLayer(layer, tracked.anchor)
  }
}

function untrack(layer: HTMLElement): void {
  const tracked = trackedLayers.get(layer)
  if (!tracked) return
  tracked.resizeObserver.disconnect()
  trackedLayers.delete(layer)
  clearInlinePosition(layer)
  if (trackedLayers.size === 0) stopViewportListeners()
}

function track(layer: HTMLElement, anchor: HTMLElement): void {
  // The layer's own size drives its placement, and it can change while open -- typeahead
  // results filter down, a submenu expands -- so re-run the placement when it does.
  const resizeObserver = new ResizeObserver(() => {
    const tracked = trackedLayers.get(layer)
    if (tracked) positionLayer(layer, tracked.anchor)
  })
  resizeObserver.observe(layer)
  trackedLayers.set(layer, { anchor, resizeObserver })
  if (trackedLayers.size === 1) startViewportListeners()
}

let viewportListenersActive = false

function startViewportListeners(): void {
  if (viewportListenersActive) return
  viewportListenersActive = true
  // Capture, so scrolling any container -- not just the page -- keeps the layer with its
  // trigger, the way real anchor positioning does.
  document.addEventListener('scroll', repositionAll, { capture: true, passive: true })
  window.addEventListener('resize', repositionAll, { passive: true })
}

function stopViewportListeners(): void {
  if (!viewportListenersActive) return
  viewportListenersActive = false
  document.removeEventListener('scroll', repositionAll, { capture: true })
  window.removeEventListener('resize', repositionAll)
}

/** Only Astryx's anchor-positioned layers carry a position-anchor; leave other popovers alone. */
function anchorIdOf(target: EventTarget | null): string | null {
  if (!(target instanceof HTMLElement) || !target.hasAttribute('popover')) return null
  return readAnchorStyle(target, 'positionAnchor') || null
}

function handleBeforeToggle(event: Event): void {
  if ((event as ToggleEvent).newState !== 'open') return
  if (!anchorIdOf(event.target)) return
  parkOffScreen(event.target as HTMLElement)
}

function handleToggle(event: Event): void {
  const layer = event.target
  if (!(layer instanceof HTMLElement) || !layer.hasAttribute('popover')) return

  if ((event as ToggleEvent).newState !== 'open') {
    untrack(layer)
    return
  }

  const anchorId = readAnchorStyle(layer, 'positionAnchor')
  if (!anchorId) return

  const anchor = findAnchorElement(anchorId)
  if (!anchor) {
    // Undo the park -- a layer left off-screen would be worse than a misplaced one.
    clearInlinePosition(layer)
    return
  }

  track(layer, anchor)
  if (!positionLayer(layer, anchor)) {
    // Nothing to anchor to. Untracking clears the off-screen park, so the layer is
    // visible (if misplaced) rather than parked out of sight.
    untrack(layer)
    return
  }
  // Content that lands after the open (async typeahead results, a lazily measured icon)
  // changes the layer's size before the ResizeObserver's first callback; re-measure once
  // the frame has settled.
  requestAnimationFrame(() => {
    const tracked = trackedLayers.get(layer)
    if (tracked) positionLayer(layer, tracked.anchor)
  })
}

/**
 * Start positioning Astryx layers by hand. No-op on browsers with CSS anchor positioning,
 * where the stylesheet already does the job. Returns a teardown function.
 */
export function installAnchorPositionFallback(): () => void {
  if (typeof document === 'undefined' || isAnchorPositioningSupported()) {
    return () => {}
  }

  // Neither `toggle` nor `beforetoggle` bubbles, so listen in the capture phase to see
  // every popover's.
  document.addEventListener('beforetoggle', handleBeforeToggle, true)
  document.addEventListener('toggle', handleToggle, true)

  return () => {
    document.removeEventListener('beforetoggle', handleBeforeToggle, true)
    document.removeEventListener('toggle', handleToggle, true)
    for (const layer of [...trackedLayers.keys()]) untrack(layer)
    stopViewportListeners()
  }
}
